// src/components/Menu/MenuMobile.tsx

import React, { ComponentType, SVGProps } from 'react';
import styled from 'styled-components';
import { IoMenu } from 'react-icons/io5';

import { MEDIA_BREAKPOINTS } from '@/constants/layout';

import ToggleThemeButton from '@/components/ToggleThemeButton/ToggleThemeButton';
import SocialMediaMenu from '@/components/SocialMediaMenu/SocialMediaMenu';
import ToggleLanguageButton from '@/components/ToggleLanguageButton/ToggleLanguageButton';
import { MenuButton } from '@/components/StyledComponents/Buttons';

type MenuVariant = 'default' | 'planning';

// ==========================================================
// STYLES
// ==========================================================

const CollapsibleMenu = styled.div<{
  $isActive: boolean;
  $variant: MenuVariant;
}>`
  visibility: ${({ $isActive }) => ($isActive ? 'visible' : 'hidden')};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};

  transform: translateY(${({ $isActive }) => ($isActive ? '0' : '-10px')});

  transition:
    visibility 0s,
    opacity 0.3s ease-out,
    transform 0.3s ease-out;

  display: flex;
  flex-direction: column;
  align-items: center;

  position: fixed;
  top: 50px;
  right: 15px;

  background-color: ${({ theme, $variant }) =>
    $variant === 'planning' ? 'var(--planning-header-bg)' : theme.menuColor};
  backdrop-filter: ${({ $variant }) =>
    $variant === 'planning' ? 'blur(14px)' : 'none'};

  width: 140px;

  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  border: 2px solid
    ${({ theme, $variant }) =>
      $variant === 'planning'
        ? 'var(--planning-border-strong)'
        : (theme.mobileMenuIconBackground ?? theme.MenuItemSelectedColor)};
  border-radius: 12px;

  z-index: 1100;
  padding: 6px 0;
`;

const MenuFooter = styled.div<{ $variant: MenuVariant }>`
  width: 100%;
  border-top: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'planning'
        ? 'var(--planning-border-muted)'
        : `${theme.textColor}33`};

  margin-top: 6px;
  padding: 6px;

  display: flex;
  justify-content: center;
  gap: 8px;

  ${({ $variant }) =>
    $variant === 'planning'
      ? `
      & > div,
      & > button {
        background-color: var(--planning-control-bg);
        color: var(--planning-menu-ink);
        border-color: var(--planning-control-border);
      }

      & svg {
        color: var(--planning-accent-soft);
      }
    `
      : ''}
`;

const MenuToggleButton = styled.button<{ $variant: MenuVariant }>`
  cursor: pointer;

  position: fixed;
  top: 10px;
  right: 10px;

  z-index: 1100;

  color: ${({ theme, $variant }) =>
    $variant === 'planning' ? 'var(--planning-menu-ink)' : theme.iconColor};

  width: ${({ theme }) => `calc(${theme.sizes.small.iconWidth} + 4px)`};
  height: ${({ theme }) => `calc(${theme.sizes.small.iconHeight} + 4px)`};
  padding: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: ${({ theme, $variant }) =>
    $variant === 'planning'
      ? 'var(--planning-header-bg)'
      : (theme.mobileMenuIconBackground ??
        `linear-gradient(100deg, ${theme.gradientColors[0]}, ${theme.gradientColors[1]})`)};

  border-radius: 50%;
  border: 2px solid
    ${({ theme, $variant }) =>
      $variant === 'planning'
        ? 'var(--planning-border-strong)'
        : theme.backgroundColor};
  box-shadow: ${({ $variant }) =>
    $variant === 'planning'
      ? '0 12px 28px var(--planning-shadow-strong)'
      : 'none'};

  &:focus-visible {
    outline: 2px solid
      ${({ theme, $variant }) =>
        $variant === 'planning'
          ? 'var(--planning-accent)'
          : theme.MenuItemSelectedColor};
    outline-offset: 3px;
  }
`;

const IoMenuIcon = styled(IoMenu as ComponentType<SVGProps<SVGSVGElement>>)`
  width: 100%;
  height: 100%;
`;

const MediaQuery = styled.div`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: block;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

const SocialsContainer = styled.div`
  position: fixed;
  bottom: 10px;
  left: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
`;

const MobileMenuButton = styled(MenuButton)<{ $variant: MenuVariant }>`
  ${({ $variant, $isSelected }) =>
    $variant === 'planning'
      ? `
      width: calc(100% - 14px);
      min-height: 38px;
      margin: 2px 0;
      color: ${
        $isSelected ? 'var(--planning-on-accent)' : 'var(--planning-menu-ink)'
      };
      background-color: ${
        $isSelected ? 'var(--planning-accent)' : 'var(--planning-button-bg)'
      };
      border: 1px solid var(--planning-border);

      &:hover,
      &:focus-visible {
        color: var(--planning-button-hover-ink);
        background-color: var(--planning-button-hover-bg);
        outline: 2px solid var(--planning-border-strong);
        outline-offset: 2px;
      }
    `
      : ''}

  html:not(.rtl) & {
    font-size: 15px;
  }
`;

// ==========================================================
// TYPES
// ==========================================================

export type MenuMobileProps<T extends string = string> = {
  sections: {
    id: T;
    label: string;
  }[];

  isMenuOpen: boolean;
  toggleMenu: () => void;

  selectedSection: T;

  scrollToSection: (sectionId: T) => void;

  variant?: MenuVariant;
};

// ==========================================================
// COMPONENT
// ==========================================================

const MenuMobile = <T extends string>({
  sections,
  isMenuOpen,
  toggleMenu,
  selectedSection,
  scrollToSection,
  variant = 'default',
}: MenuMobileProps<T>) => {
  const handleMenuItemClick = (id: T) => {
    scrollToSection(id);

    const hash = `#${encodeURIComponent(id)}`;
    window.history.replaceState({}, '', hash);

    toggleMenu();
  };

  return (
    <MediaQuery>
      <MenuToggleButton
        $variant={variant}
        type="button"
        onClick={toggleMenu}
        aria-label={isMenuOpen ? 'סגור תפריט' : 'פתח תפריט'}
        aria-expanded={isMenuOpen}
        aria-controls="mobile-main-menu"
      >
        <IoMenuIcon aria-hidden="true" focusable="false" />
      </MenuToggleButton>

      <SocialsContainer>
        <SocialMediaMenu orientation="vertical" />
      </SocialsContainer>

      <CollapsibleMenu
        id="mobile-main-menu"
        $isActive={isMenuOpen}
        $variant={variant}
        role="navigation"
        aria-label="תפריט ראשי (מובייל)"
      >
        {/* ===== SECTIONS ===== */}
        {sections.map(s => {
          const isActive = selectedSection === s.id;

          return (
            <MobileMenuButton
              key={s.id}
              $isSelected={isActive}
              $variant={variant}
              onClick={() => handleMenuItemClick(s.id)}
              aria-current={isActive ? 'page' : undefined}
            >
              {s.label}
            </MobileMenuButton>
          );
        })}

        {/* ===== FOOTER CONTROLS ===== */}
        <MenuFooter $variant={variant}>
          <ToggleLanguageButton
            onToggle={toggleMenu}
            tone={variant === 'planning' ? 'light' : 'default'}
          />

          <ToggleThemeButton onToggle={toggleMenu} />
        </MenuFooter>
      </CollapsibleMenu>
    </MediaQuery>
  );
};

export default MenuMobile;
